/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { REFERENCE_RELATION, RelatesToRelationship } from './extensible_events';
import type { MAssetEvent, MLocationEvent, MTimestampEvent } from './location';
import { UnstableValue } from '../utils/NamespacedValue';

/**
 * Beacon info and beacon event types as described in MSC3672
 * https://github.com/matrix-org/matrix-spec-proposals/pull/3672
 */

/**
 * Beacon info events are state events.
 * We have two requirements for these events:
 * 1. they can only be written by their owner
 * 2. a user can have an arbitrary number of beacon_info events
 *
 * 1. is achieved by setting the state_key to the owners mxid.
 * Event keys in room state are a combination of `type` + `state_key`.
 * To achieve an arbitrary number of only owner-writable state events
 * we introduce a variable suffix to the event type
 *
 * @example
 * ```
 * {
 *      "type": "m.beacon_info.@matthew:matrix.org.1",
 *      "state_key": "@matthew:matrix.org",
 *      "content": {
 *          "m.beacon_info": {
 *              "description": "The Matthew Tracker",
 *              "timeout": 86400000,
 *          },
 *          // more content as described below
 *      }
 * },
 * {
 *      "type": "m.beacon_info.@matthew:matrix.org.2",
 *      "state_key": "@matthew:matrix.org",
 *      "content": {
 *          "m.beacon_info": {
 *              "description": "Another different Matthew tracker",
 *              "timeout": 400000,
 *          },
 *          // more content as described below
 *      }
 * }
 * ```
 */

export type MBeaconInfoContent = {
  description?: string;
  // how long from the last event until we consider the beacon inactive in milliseconds
  timeout: number;
  // true when this is a live location beacon
  // https://github.com/matrix-org/matrix-spec-proposals/pull/3672
  live?: boolean;
};

/**
 * m.beacon_info.* event content
 */
export type MBeaconInfoEventContent = MBeaconInfoContent
  // creation timestamp of the beacon on the client
  & MTimestampEvent
  // the type of asset being tracked as per MSC3488
  & MAssetEvent;

/**
 * Content of an m.beacon event
 */
export type MBeaconEventContent = MLocationEvent
  // timestamp when location was taken
  & MTimestampEvent
  // relates to a beacon_info event
  & RelatesToRelationship<typeof REFERENCE_RELATION>;

/**
 * Non-variable type for m.beacon_info event content
 */
export const M_BEACON_INFO = new UnstableValue('m.beacon_info', 'org.matrix.msc3672.beacon_info');
export const M_BEACON = new UnstableValue('m.beacon', 'org.matrix.msc3672.beacon');
